/**
 * Auth API Client
 * ================
 * API functions for authentication operations.
 */

import { apiClient, getErrorMessage } from './client';
import type { User } from '@/types';

// =============================================================================
// TYPES
// =============================================================================

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: User;
}

export interface RegisterRequest {
  email: string;
  password: string;
  confirm_password: string;
  first_name: string;
  last_name: string;
  organization_name: string;
  organization_phone?: string;
  accept_terms: boolean;
  accept_privacy: boolean;
}

export interface RegisterResponse {
  message: string;
  user_id: string;
  tenant_id: string;
  requires_verification: boolean;
}

export interface SSOExchangeRequest {
  sso_token: string;
  patient_id?: string;
}

export interface SSOExchangeResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: User;
  patient_id?: string;
}

export interface RefreshTokenRequest {
  refresh_token: string;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export interface PasswordChangeRequest {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

export interface APIKeyCreateRequest {
  name: string;
  description?: string;
  environment?: string;
  expires_in_days?: number;
  scopes?: string[];
}

export interface APIKeyResponse {
  id: string;
  name: string;
  key?: string; // Only present on creation
  key_prefix: string;
  environment: string;
  expires_at?: string;
  scopes: string[];
  created_at: string;
  last_used_at?: string;
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Login with email and password.
 */
export async function login(data: LoginRequest): Promise<LoginResponse> {
  const response = await apiClient.post<LoginResponse>('/auth/login', data);
  return response.data;
}

/**
 * Register a new user and organization.
 */
export async function register(data: RegisterRequest): Promise<RegisterResponse> {
  const response = await apiClient.post<RegisterResponse>('/auth/register', data);
  return response.data;
}

/**
 * Exchange SSO token for internal JWT.
 */
export async function exchangeSSO(data: SSOExchangeRequest): Promise<SSOExchangeResponse> {
  const response = await apiClient.post<SSOExchangeResponse>('/auth/sso/exchange', data);
  return response.data;
}

/**
 * Refresh access token.
 */
export async function refreshToken(data: RefreshTokenRequest): Promise<TokenResponse> {
  const response = await apiClient.post<TokenResponse>('/auth/refresh', data);
  return response.data;
}

/**
 * Logout (invalidate session on server).
 */
export async function logout(): Promise<void> {
  await apiClient.post('/auth/logout');
}

/**
 * Get current user info.
 */
export async function getCurrentUser(): Promise<User> {
  const response = await apiClient.get<User>('/auth/me');
  return response.data;
}

/**
 * Change password.
 */
export async function changePassword(data: PasswordChangeRequest): Promise<void> {
  await apiClient.post('/auth/password/change', data);
}

/**
 * Request password reset email.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  await apiClient.post('/auth/password/reset-request', { email });
}

/**
 * Confirm password reset with token.
 */
export async function confirmPasswordReset(token: string, newPassword: string): Promise<void> {
  await apiClient.post('/auth/password/reset-confirm', {
    token,
    new_password: newPassword,
  });
}

/**
 * List API keys.
 */
export async function listAPIKeys(): Promise<APIKeyResponse[]> {
  const response = await apiClient.get<APIKeyResponse[]>('/auth/api-keys');
  return response.data;
}

/**
 * Create API key.
 */
export async function createAPIKey(data: APIKeyCreateRequest): Promise<APIKeyResponse> {
  const response = await apiClient.post<APIKeyResponse>('/auth/api-keys', data);
  return response.data;
}

/**
 * Revoke API key.
 */
export async function revokeAPIKey(keyId: string): Promise<void> {
  await apiClient.delete(`/auth/api-keys/${keyId}`);
}

/**
 * Update user profile.
 */
export async function updateProfile(data: { first_name: string; last_name: string }): Promise<User> {
  const response = await apiClient.patch<User>('/auth/profile', data);
  return response.data;
}

/**
 * Upload profile image.
 */
export async function uploadProfileImage(formData: FormData): Promise<User> {
  const response = await apiClient.post<User>('/auth/profile/image', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}
